import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import StatusBadge from './StatusBadge';
import ProductDetailsModal from './ProductDetailsModal';
import { groupPublicationHistory } from '../publicationRunHistory.cjs';

const HistoryTable = ({ data, onRetry, onDelete, onViewDetails }) => {
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [selectedModal, setSelectedModal] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: 'date', direction: 'desc' });

  const toggleRowExpansion = (id) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded?.has(id)) {
      newExpanded?.delete(id);
    } else {
      newExpanded?.add(id);
    }
    setExpandedRows(newExpanded);
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig?.key === key && sortConfig?.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortedData = () => {
    return [...data]?.sort((a, b) => {
      if (sortConfig?.key === 'date') {
        const dateA = new Date(a.date);
        const dateB = new Date(b.date);
        return sortConfig?.direction === 'asc' ? dateA - dateB : dateB - dateA;
      }
      
      if (sortConfig?.key === 'fileName') {
        return sortConfig?.direction === 'asc' 
          ? a?.fileName?.localeCompare(b?.fileName)
          : b?.fileName?.localeCompare(a?.fileName);
      }
      
      if (sortConfig?.key === 'totalProducts' || sortConfig?.key === 'successCount' || sortConfig?.key === 'failedCount') {
        return sortConfig?.direction === 'asc' 
          ? a?.[sortConfig?.key] - b?.[sortConfig?.key]
          : b?.[sortConfig?.key] - a?.[sortConfig?.key];
      }
      
      return 0;
    });
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date?.toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getSuccessRate = (success, total) => {
    if (total === 0) return 0;
    return Math.round((success / total) * 100);
  };

  const SortableHeader = ({ label, sortKey, className = "" }) => (
    <th 
      className={`px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider cursor-pointer hover:bg-muted/50 transition-colors ${className}`}
      onClick={() => handleSort(sortKey)}
    >
      <div className="flex items-center space-x-1">
        <span>{label}</span>
        <div className="flex flex-col">
          <Icon 
            name="ChevronUp" 
            size={12} 
            className={`${sortConfig?.key === sortKey && sortConfig?.direction === 'asc' ? 'text-primary' : 'text-muted-foreground'}`}
          />
          <Icon 
            name="ChevronDown" 
            size={12} 
            className={`-mt-1 ${sortConfig?.key === sortKey && sortConfig?.direction === 'desc' ? 'text-primary' : 'text-muted-foreground'}`}
          />
        </div>
      </div>
    </th>
  );

  const topScrollRef = React.useRef(null);
  const tableScrollRef = React.useRef(null);
  const topTrackRef = React.useRef(null);

  React.useEffect(() => {
    const top = topScrollRef.current;
    const tableContainer = tableScrollRef.current;
    const track = topTrackRef.current;

    if (!top || !tableContainer || !track) return;

    const updateWidth = () => {
      track.style.width = `${tableContainer.scrollWidth}px`;
      top.scrollLeft = tableContainer.scrollLeft;
    };

    const observer = typeof ResizeObserver !== 'undefined'
      ? new ResizeObserver(updateWidth)
      : null;

    observer?.observe(tableContainer);

    const table = tableContainer.querySelector('table');
    if (table) observer?.observe(table);

    window.addEventListener('resize', updateWidth);
    updateWidth();

    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', updateWidth);
    };
  }, []);

  const sortedData = getSortedData();
  const publicationGroups = groupPublicationHistory(sortedData);

  const formatRunDate = (dateString) => formatDate(dateString);

  const PublicationRunSummary = ({ group, mobile = false }) => {
    if (!group?.publicationRunId || !group?.isComplete) return null;

    const { summary } = group;

    const downloadPublicationErrors = (event) => {
      event.preventDefault();
      event.stopPropagation();

      const errors = Array.isArray(summary?.errors)
        ? summary.errors
        : [];

      if (!errors.length) return;

      const labels = {
        product: 'Producto',
        fileName: 'Archivo',
        asin: 'ASIN',
        productId: 'ID de producto',
        id: 'ID',
        error: 'Error',
        message: 'Mensaje',
        details: 'Detalles',
        reason: 'Motivo',
        code: 'Código'
      };

      const formatValue = (value) => {
        if (value === null || value === undefined) return '';
        if (typeof value === 'string') return value;
        return JSON.stringify(value, null, 2);
      };

      const lines = [
        'MERCABULL - ERRORES DE PUBLICACIÓN',
        '========================================',
        '',
        `Publicación: ${group?.publicationRunId || 'Sin ID'}`,
        `Total procesados: ${summary?.totalProducts ?? 0}`,
        `Publicados correctamente: ${summary?.successCount ?? 0}`,
        `No publicados: ${summary?.failedCount ?? 0}`,
        `Errores incluidos: ${errors.length}`,
        '',
        '========================================',
        ''
      ];

      errors.forEach((item, index) => {
        lines.push(`ERROR ${index + 1}`);
        lines.push('----------------------------------------');

        if (item && typeof item === 'object') {
          Object.entries(item).forEach(([key, value]) => {
            // Evitar incluir credenciales en un archivo compartible
            if (/token|secret|password|authorization|credential|api[_-]?key/i.test(key)) {
              return;
            }

            lines.push(
              `${labels[key] || key}: ${formatValue(value)}`
            );
          });
        } else {
          lines.push(`Error: ${formatValue(item)}`);
        }

        lines.push('');
      });

      const blob = new Blob(
        ['\uFEFF', lines.join('\n')],
        { type: 'text/plain;charset=utf-8' }
      );

      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');

      const runId = String(group?.publicationRunId || 'lote')
        .replace(/[^a-zA-Z0-9_-]/g, '_')
        .slice(0, 70);

      link.href = url;
      link.download = `MercaBull_Errores_${runId}.txt`;

      document.body.appendChild(link);
      link.click();
      link.remove();

      window.setTimeout(() => URL.revokeObjectURL(url), 1000);
    };

    const content = (
      <div className="border-t border-border pt-4 mt-4">
        <h3 className="text-sm font-semibold text-text-primary uppercase">Resumen de la publicación</h3>
        <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
          <p className="text-text-primary">Total procesados: <span className="font-semibold">{summary?.totalProducts}</span></p>
          <p className="text-success">Publicados correctamente: <span className="font-semibold">{summary?.successCount}</span></p>
          <p className="text-error">Errores / no publicados: <span className="font-semibold">{summary?.failedCount}</span></p>
        </div>
        {summary?.errors?.length > 0 && (
          <details className="group mt-4 border border-border rounded-lg overflow-hidden">
            <summary className="flex items-center justify-between gap-3 px-4 py-3 cursor-pointer select-none bg-muted/20 hover:bg-muted/40 transition-colors list-none">
              <div className="flex items-center gap-2">
                <Icon
                  name="ChevronRight"
                  size={17}
                  className="text-text-secondary transition-transform duration-200 group-open:rotate-90"
                />
                <span className="text-sm font-semibold text-text-primary uppercase">
                  Errores de esta publicación
                </span>
              </div>

              <div className="flex items-center gap-2 shrink-0">
                <button
                  type="button"
                  onClick={downloadPublicationErrors}
                  className="inline-flex items-center gap-2 rounded-md bg-blue-800 px-3 py-2 text-xs font-semibold text-primary-foreground hover:bg-blue-900 transition-colors"
                  title="Descargar los errores de esta publicación"
                >
                  <Icon name="Download" size={15} />
                  Descargar errores
                </button>
                <span className="text-xs font-semibold text-error bg-error/10 px-2.5 py-1 rounded-full">
                  {summary.errors.length}
                </span>
              </div>
            </summary>

            <div className="px-4 py-4 border-t border-border bg-surface">
              <ol className="space-y-3 text-sm text-text-primary">
                {summary.errors.map((error, index) => (
                  <li
                    key={`${error?.fileName}-${index}`}
                    className="border-l-2 border-error pl-3"
                  >
                    <p>
                      <span className="font-medium">Producto:</span>{' '}
                      {error?.product}
                    </p>

                    <p>
                      <span className="font-medium">Archivo:</span>{' '}
                      {error?.fileName}
                    </p>

                    {error?.itemId && (
                      <p>
                        <span className="font-medium">ID:</span>{' '}
                        {error.itemId}
                      </p>
                    )}

                    <p
                      className="whitespace-pre-wrap break-words"
                      style={{
                        overflowWrap: 'anywhere',
                        maxWidth: 'calc(100vw - 8rem)'
                      }}
                    >
                      <span className="font-medium">Error:</span>{' '}
                      {error?.error}
                    </p>
                  </li>
                ))}
              </ol>
            </div>
          </details>
        )}
      </div>
    );
    return mobile ? <div className="px-4 pb-4">{content}</div> : <tr><td colSpan="9" className="px-6 py-4 bg-muted/10">{content}</td></tr>;
  };

  return (
    <>
      <div className="bg-surface border border-border rounded-lg overflow-hidden">
        {/* Desktop Table */}
        <div
          ref={topScrollRef}
          onScroll={(e) => {
            if (tableScrollRef.current) {
              tableScrollRef.current.scrollLeft = e.currentTarget.scrollLeft;
            }
          }}
          className="hidden lg:block h-5 overflow-x-auto border-b border-border bg-muted/20"
          aria-label="Desplazamiento horizontal del historial"
        >
          <div ref={topTrackRef} className="h-px" />
        </div>

        <div
          ref={tableScrollRef}
          onScroll={(e) => {
            if (topScrollRef.current) {
              topScrollRef.current.scrollLeft = e.currentTarget.scrollLeft;
            }
          }}
          className="hidden lg:block overflow-x-auto"
        >
          <table className="min-w-full divide-y divide-border">
            <thead className="bg-muted/30">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider w-12">
                  <span className="sr-only">Expandir</span>
                </th>
                <SortableHeader label="Fecha y Hora" sortKey="date" />
                <SortableHeader label="Archivo" sortKey="fileName" />
                <SortableHeader label="Total" sortKey="totalProducts" />
                <SortableHeader label="Exitosos" sortKey="successCount" />
                <SortableHeader label="Fallidos" sortKey="failedCount" />
                <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">
                  Estado
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">
                  Tasa Éxito
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="bg-surface divide-y divide-border">
              {publicationGroups?.flatMap((group) => [
                ...(group?.publicationRunId ? [
                  <tr key={`run-${group.publicationRunId}`} className="bg-muted/20 border-t-2 border-border">
                    <td colSpan="9" className="px-6 py-3 text-sm font-semibold text-text-primary uppercase">Publicación - {formatRunDate(group.startedAt)}</td>
                  </tr>
                ] : []),
                ...group.entries.map((batch) => (
                <React.Fragment key={batch?.id}>
                  <tr className="hover:bg-muted/20 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        onClick={() => toggleRowExpansion(batch?.id)}
                        className="p-1 hover:bg-muted rounded-md transition-colors"
                      >
                        <Icon 
                          name={expandedRows?.has(batch?.id) ? "ChevronDown" : "ChevronRight"} 
                          size={16} 
                          className="text-text-secondary"
                        />
                      </button>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-text-primary">
                      {formatDate(batch?.date)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <Icon name="FileText" size={16} className="text-text-secondary mr-2" />
                        <span className="text-sm text-text-primary font-medium">{batch?.fileName}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-text-primary font-medium">
                      {batch?.totalProducts}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-success font-medium">
                      {batch?.successCount}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-error font-medium">
                      {batch?.failedCount}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <StatusBadge status={batch?.status} />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="w-16 bg-muted rounded-full h-2 mr-2">
                          <div 
                            className="bg-success h-2 rounded-full transition-all duration-300"
                            style={{ width: `${getSuccessRate(batch?.successCount, batch?.totalProducts)}%` }}
                          ></div>
                        </div>
                        <span className="text-sm text-text-secondary">
                          {getSuccessRate(batch?.successCount, batch?.totalProducts)}%
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center space-x-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setSelectedModal(batch)}
                          iconName="Eye"
                        >
                          Ver
                        </Button>
                        {batch?.status === 'failed' && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => onRetry(batch?.id)}
                            iconName="RotateCcw"
                          >
                            Reintentar
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onDelete(batch?.id)}
                          iconName="Trash2"
                          className="text-error hover:text-error"
                        >
                          Eliminar
                        </Button>
                      </div>
                    </td>
                  </tr>
                  
                  {expandedRows?.has(batch?.id) && (
                    <tr>
                      <td colSpan="9" className="px-6 py-4 bg-muted/10">
                        <div className="space-y-3">
                          <h4 className="font-medium text-text-primary">Detalles del Lote</h4>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                            <div>
                              <span className="font-medium text-text-primary">Duración:</span>
                              <p className="text-text-secondary">{batch?.duration}</p>
                            </div>
                            <div>
                              <span className="font-medium text-text-primary">Tamaño del archivo:</span>
                              <p className="text-text-secondary">{batch?.fileSize}</p>
                            </div>
                            <div>
                              <span className="font-medium text-text-primary">Usuario:</span>
                              <p className="text-text-secondary">{batch?.uploadedBy}</p>
                            </div>
                          </div>
                          {false && batch?.errorSummary && (
                            <div>
                              <span className="font-medium text-text-primary">Errores principales:</span>
                              <ul className="mt-1 text-sm text-error">
                                {batch?.errorSummary?.map((error, index) => (
                                  <li key={index} className="flex items-center">
                                    <Icon name="AlertCircle" size={12} className="mr-1" />
                                    {error}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
                )),
                <PublicationRunSummary key={`summary-${group.publicationRunId || group.entries?.[0]?.id}`} group={group} />
              ])}
            </tbody>
          </table>
        </div>

        {/* Mobile Cards */}
        <div className="lg:hidden">
          {publicationGroups?.map((group) => (
            <React.Fragment key={group.publicationRunId || group.entries?.[0]?.id}>
              {group?.publicationRunId && (
                <div className="px-4 py-3 border-b border-border bg-muted/20 text-sm font-semibold text-text-primary uppercase">
                  Publicación - {formatRunDate(group.startedAt)}
                </div>
              )}
              {group.entries.map((batch) => (
            <div key={batch?.id} className="border-b border-border last:border-b-0">
              <div className="p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center space-x-2">
                    <Icon name="FileText" size={16} className="text-text-secondary" />
                    <span className="font-medium text-text-primary truncate">{batch?.fileName}</span>
                  </div>
                  <StatusBadge status={batch?.status} size="sm" />
                </div>
                
                <div className="grid grid-cols-2 gap-3 mb-3 text-sm">
                  <div>
                    <span className="text-text-secondary">Fecha:</span>
                    <p className="text-text-primary font-medium">{formatDate(batch?.date)}</p>
                  </div>
                  <div>
                    <span className="text-text-secondary">Total:</span>
                    <p className="text-text-primary font-medium">{batch?.totalProducts} productos</p>
                  </div>
                  <div>
                    <span className="text-text-secondary">Exitosos:</span>
                    <p className="text-success font-medium">{batch?.successCount}</p>
                  </div>
                  <div>
                    <span className="text-text-secondary">Fallidos:</span>
                    <p className="text-error font-medium">{batch?.failedCount}</p>
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <div className="w-20 bg-muted rounded-full h-2">
                      <div 
                        className="bg-success h-2 rounded-full transition-all duration-300"
                        style={{ width: `${getSuccessRate(batch?.successCount, batch?.totalProducts)}%` }}
                      ></div>
                    </div>
                    <span className="text-sm text-text-secondary">
                      {getSuccessRate(batch?.successCount, batch?.totalProducts)}%
                    </span>
                  </div>
                  
                  <div className="flex items-center space-x-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setSelectedModal(batch)}
                      iconName="Eye"
                    />
                    {batch?.status === 'failed' && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onRetry(batch?.id)}
                        iconName="RotateCcw"
                      />
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onDelete(batch?.id)}
                      iconName="Trash2"
                      className="text-error hover:text-error"
                    />
                  </div>
                </div>
              </div>
            </div>
              ))}
              <PublicationRunSummary group={group} mobile />
            </React.Fragment>
          ))}
        </div>
      </div>
      {selectedModal && (
        <ProductDetailsModal
          batch={selectedModal}
          onClose={() => setSelectedModal(null)}
          onRetryProduct={(productId) => console.log('Retry product:', productId)}
        />
      )}
    </>
  );
};

export default HistoryTable;